import * as React from "react"
import { format } from "date-fns"
import { CalendarIcon, Clock } from "lucide-react"
import { Button } from "@/Components/ui/button"
import { Calendar } from "@/Components/ui/calendar"
import { TimePicker } from "@/Components/ui/time-picker"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/Components/ui/popover"
import { cn } from "@/lib/utils"
import { Separator } from "@/Components/ui/separator"

export function DateTimePicker({ 
  value, 
  onChange, 
  placeholder = "Pick a date and time",
  disabled,
  minDate,
  className,
  id,
  ...props 
}) {
  const [open, setOpen] = React.useState(false)
  const [date, setDate] = React.useState(value ? new Date(value) : undefined)
  const [timeValue, setTimeValue] = React.useState(
    value ? format(new Date(value), "HH:mm") : "09:00"
  )

  React.useEffect(() => {
    if (value) {
      const newDate = new Date(value)
      if (!isNaN(newDate.getTime())) {
        setDate(newDate)
        setTimeValue(format(newDate, "HH:mm"))
      }
    } else {
      setDate(undefined)
      setTimeValue("09:00")
    }
  }, [value])

  // Set default to current date and time when popover opens (only if no value exists and no date is selected)
  React.useEffect(() => {
    if (open && !value && !date) {
      const now = new Date()
      // Set time to exact current time (no rounding)
      const currentTime = new Date(now)
      
      // Set to current date and time when opening for the first time
      setDate(now)
      const timeStr = format(currentTime, "HH:mm")
      setTimeValue(timeStr)
      
      // Auto-update the value with current date and time
      const finalDate = new Date(now)
      finalDate.setHours(currentTime.getHours(), currentTime.getMinutes(), 0, 0)
      const formatted = format(finalDate, "yyyy-MM-dd'T'HH:mm")
      onChange?.(formatted)
    }
  }, [open])

  const handleDateSelect = (selectedDate) => {
    if (selectedDate) {
      const newDate = new Date(selectedDate)
      // Preserve or set time - handle 0 (midnight) properly
      const [hours, minutes] = timeValue.split(":").map(Number)
      const hoursValue = isNaN(hours) ? 9 : hours
      const minutesValue = isNaN(minutes) ? 0 : minutes
      newDate.setHours(hoursValue, minutesValue, 0, 0)
      setDate(newDate)
      updateDateTime(newDate, timeValue)
    }
  }

  const handleTimeChange = (time) => {
    setTimeValue(time)
    if (date) {
      const [hours, minutes] = time.split(":").map(Number)
      const newDate = new Date(date)
      // Handle 0 (midnight) properly - don't use || operator as 0 is falsy
      const hoursValue = isNaN(hours) ? 9 : hours
      const minutesValue = isNaN(minutes) ? 0 : minutes
      newDate.setHours(hoursValue, minutesValue, 0, 0)
      setDate(newDate)
      updateDateTime(newDate, time)
    }
  }

  const updateDateTime = (dateObj, timeStr) => {
    if (dateObj && timeStr) {
      const [hours, minutes] = timeStr.split(":").map(Number)
      const finalDate = new Date(dateObj)
      // Handle 0 (midnight) properly - don't use || operator as 0 is falsy
      const hoursValue = isNaN(hours) ? 9 : hours
      const minutesValue = isNaN(minutes) ? 0 : minutes
      finalDate.setHours(hoursValue, minutesValue, 0, 0)
      // Format as ISO string for the form value
      const formatted = format(finalDate, "yyyy-MM-dd'T'HH:mm")
      onChange?.(formatted)
    }
  }

  const displayValue = date 
    ? `${format(date, "MMM d, yyyy")} at ${format(date, "h:mm a")}`
    : placeholder

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground",
            className
          )}
          disabled={disabled}
          {...props}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayValue}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-col sm:flex-row">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            disabled={minDate ? (date) => {
              if (!date) return false;
              const minDateObj = new Date(minDate);
              const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
              const minDateOnly = new Date(minDateObj.getFullYear(), minDateObj.getMonth(), minDateObj.getDate());
              return dateOnly < minDateOnly;
            } : undefined}
            initialFocus
          />
          <div className="border-t sm:border-l sm:border-t-0">
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Clock className="h-4 w-4" />
                <span>Time</span>
              </div>
              <Separator />
              <div className="space-y-3">
                <TimePicker
                  value={timeValue}
                  onChange={handleTimeChange}
                  disabled={!date}
                />
                {date && timeValue && (() => {
                  const previewDate = new Date(date)
                  const [hours, minutes] = timeValue.split(":").map(Number)
                  // Handle 0 (midnight) properly - don't use || operator as 0 is falsy
                  const hoursValue = isNaN(hours) ? 9 : hours
                  const minutesValue = isNaN(minutes) ? 0 : minutes
                  previewDate.setHours(hoursValue, minutesValue, 0, 0)
                  return (
                    <div className="text-xs text-muted-foreground mt-2 p-2 bg-muted rounded-md text-center">
                      {format(previewDate, "PPpp")}
                    </div>
                  )
                })()}
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
